import * as THREE from 'three';
import { geometryBoundsVersions } from './GeometryBounds';

// Keep the thin wave band on both sides so the animated opaque surface never
// reveals a seam. All terrain and objects beyond it are culled before drawing.
export const CREATIVE_WATER_BAND = 0.3;
type WaterDrawable = THREE.Mesh | THREE.Line | THREE.Points | THREE.Sprite;
function isWaterDrawable(object: THREE.Object3D): object is WaterDrawable {
  return (
    object instanceof THREE.Mesh ||
    object instanceof THREE.Line ||
    object instanceof THREE.Points ||
    object instanceof THREE.Sprite
  );
}
function isWaterInstance(object: WaterDrawable): object is THREE.InstancedMesh {
  return object instanceof THREE.InstancedMesh;
}
type WaterRanges = { above: [number, number]; below: [number, number] };
const terrainRanges = new WeakMap<THREE.BufferGeometry, WaterRanges>();

/** Partition once at mesh creation; switching sides only changes the draw range. */
export function partitionCreativeWaterTerrain(geometry: THREE.BufferGeometry): void {
  const indices = geometry.getIndex();
  if (indices === null) return;
  const positions = geometry.getAttribute('position');
  const above: number[] = [],
    crossing: number[] = [],
    below: number[] = [];
  for (let i = 0; i < indices.count; i += 3) {
    const a = indices.getX(i),
      b = indices.getX(i + 1),
      c = indices.getX(i + 2);
    const min = Math.min(positions.getY(a), positions.getY(b), positions.getY(c));
    const max = Math.max(positions.getY(a), positions.getY(b), positions.getY(c));
    const target =
      min > CREATIVE_WATER_BAND ? above : max < -CREATIVE_WATER_BAND ? below : crossing;
    target.push(a, b, c);
  }
  geometry.setIndex([...above, ...crossing, ...below]);
  terrainRanges.set(geometry, {
    above: [0, above.length + crossing.length],
    below: [above.length, crossing.length + below.length],
  });
}

/** Render-only changes: keep streaming, collision, picking and authored visibility intact. */
export class CreativeWaterVisibility {
  private readonly roots = new Map<
    THREE.Object3D,
    {
      dirty: boolean;
      objects: THREE.Object3D[];
      drawables: WaterDrawable[];
      invalidate: () => void;
    }
  >();
  private readonly worldBounds = new WeakMap<
    WaterDrawable,
    {
      geometry: THREE.BufferGeometry;
      position: THREE.BufferAttribute | THREE.InterleavedBufferAttribute | undefined;
      version: number;
      instanceVersion: number;
      count: number;
      matrix: THREE.Matrix4;
      bounds: THREE.Box3;
    }
  >();

  register(root: THREE.Object3D): void {
    if (this.roots.has(root)) return;
    const entry = {
      dirty: true,
      objects: [] as THREE.Object3D[],
      drawables: [] as WaterDrawable[],
      invalidate: () => {
        entry.dirty = true;
      },
    };
    this.roots.set(root, entry);
  }

  unregister(root: THREE.Object3D): void {
    const entry = this.roots.get(root);
    if (entry === undefined) return;
    for (const object of entry.objects) {
      object.removeEventListener('childadded', entry.invalidate);
      object.removeEventListener('childremoved', entry.invalidate);
    }
    this.roots.delete(root);
  }

  dispose(): void {
    for (const root of this.roots.keys()) this.unregister(root);
  }

  private visit(root: THREE.Object3D, visit: (object: WaterDrawable) => void): void {
    if (!root.visible) return;
    const entry = this.roots.get(root);
    if (entry === undefined) {
      if (isWaterDrawable(root)) visit(root);
      for (const child of root.children) this.visit(child, visit);
      return;
    }
    if (entry.dirty) {
      for (const object of entry.objects) {
        object.removeEventListener('childadded', entry.invalidate);
        object.removeEventListener('childremoved', entry.invalidate);
      }
      entry.objects = [];
      entry.drawables = [];
      root.traverse((object) => {
        entry.objects.push(object);
        if (isWaterDrawable(object)) entry.drawables.push(object);
        object.addEventListener('childadded', entry.invalidate);
        object.addEventListener('childremoved', entry.invalidate);
      });
      entry.dirty = false;
    }
    for (const object of entry.drawables) {
      let ancestor: THREE.Object3D | null = object;
      while (ancestor !== null && ancestor !== root && ancestor.visible) ancestor = ancestor.parent;
      if (ancestor === root) visit(object);
    }
  }
  private readonly plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), CREATIVE_WATER_BAND);
  private readonly bounds = new THREE.Box3();
  private readonly sphere = new THREE.Sphere();
  private readonly geometryVersions = new WeakMap<
    THREE.BufferGeometry,
    {
      position: THREE.BufferAttribute | THREE.InterleavedBufferAttribute | undefined;
      version: number;
    }
  >();
  private readonly instanceVersions = new WeakMap<
    THREE.InstancedMesh,
    { matrix: number; geometry: number; count: number }
  >();
  private readonly layers: [THREE.Object3D, number][] = [];
  private readonly ranges: [THREE.BufferGeometry, number, number][] = [];
  private readonly materials = new Map<
    THREE.Material,
    { planes: THREE.Plane[] | null; shadows: boolean }
  >();
  readonly stats = { underwater: false, culledObjects: 0, skippedTerrainTriangles: 0 };

  render(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    water: THREE.Object3D | undefined,
    draw: () => void,
  ): void {
    const underwater = camera.position.y < 0;
    this.stats.underwater = underwater;
    this.stats.culledObjects = 0;
    this.stats.skippedTerrainTriangles = 0;
    this.plane.normal.y = underwater ? -1 : 1;
    const localClipping = renderer.localClippingEnabled;
    renderer.localClippingEnabled = true;
    scene.updateMatrixWorld();
    try {
      this.visit(scene, (object) => {
        if (object === water) return;
        if (!isWaterDrawable(object)) return;
        const geometry = object.geometry;
        const range = terrainRanges.get(geometry)?.[underwater ? 'below' : 'above'];
        if (range !== undefined) {
          this.ranges.push([geometry, geometry.drawRange.start, geometry.drawRange.count]);
          geometry.setDrawRange(...range);
          this.stats.skippedTerrainTriangles += ((geometry.index?.count ?? 0) - range[1]) / 3;
        }
        if (range?.[1] === 0 || this.onHiddenSide(object, underwater)) {
          // Layers hide this drawable without hiding potentially visible children.
          this.layers.push([object, object.layers.mask]);
          object.layers.mask = 0;
          this.stats.culledObjects++;
          return;
        }
        for (const material of Array.isArray(object.material)
          ? object.material
          : [object.material]) {
          if (this.materials.has(material)) continue;
          this.materials.set(material, {
            planes: material.clippingPlanes,
            shadows: material.clipShadows,
          });
          material.clippingPlanes = [...(material.clippingPlanes ?? []), this.plane];
          material.clipShadows = true;
        }
      });
      draw();
    } finally {
      for (const [object, mask] of this.layers) object.layers.mask = mask;
      for (const [geometry, start, count] of this.ranges) geometry.setDrawRange(start, count);
      for (const [material, state] of this.materials) {
        material.clippingPlanes = state.planes;
        material.clipShadows = state.shadows;
      }
      this.layers.length = 0;
      this.ranges.length = 0;
      this.materials.clear();
      renderer.localClippingEnabled = localClipping;
    }
  }

  private onHiddenSide(object: WaterDrawable, underwater: boolean): boolean {
    const geometry = object.geometry;
    const position = geometry.getAttribute('position') as
      THREE.BufferAttribute | THREE.InterleavedBufferAttribute | undefined;
    const version =
      position instanceof THREE.InterleavedBufferAttribute
        ? position.data.version
        : (position?.version ?? 0);
    const instanceVersion = isWaterInstance(object) ? object.instanceMatrix.version : -1;
    const count = isWaterInstance(object) ? object.count : 0;
    const cached = this.worldBounds.get(object);
    const cacheable = !(object instanceof THREE.SkinnedMesh) && !(object instanceof THREE.Sprite);
    if (
      cacheable &&
      cached?.geometry === geometry &&
      cached.position === position &&
      cached.version === version &&
      cached.instanceVersion === instanceVersion &&
      cached.count === count &&
      cached.matrix.equals(object.matrixWorld)
    ) {
      return underwater
        ? cached.bounds.min.y > CREATIVE_WATER_BAND
        : cached.bounds.max.y < -CREATIVE_WATER_BAND;
    }
    if (object instanceof THREE.Sprite) {
      this.sphere.center.setFromMatrixPosition(object.matrixWorld);
      this.sphere.radius = Math.SQRT1_2 * object.matrixWorld.getMaxScaleOnAxis();
      this.sphere.getBoundingBox(this.bounds);
    } else if (object instanceof THREE.SkinnedMesh) {
      // A current pose is needed for avatars that straddle the waterline.
      object.computeBoundingBox();
      this.bounds.copy(object.boundingBox).applyMatrix4(object.matrixWorld);
    } else {
      const previousGeometryVersion = this.geometryVersions.get(geometry);
      const geometryChanged =
        previousGeometryVersion !== undefined &&
        (previousGeometryVersion.version !== version ||
          previousGeometryVersion.position !== position);
      const produced = geometryBoundsVersions.get(geometry);
      if (
        geometry.boundingBox === null ||
        (geometryChanged && (produced?.version !== version || produced.position !== position))
      )
        geometry.computeBoundingBox();
      if (previousGeometryVersion === undefined)
        this.geometryVersions.set(geometry, { position, version });
      else {
        previousGeometryVersion.position = position;
        previousGeometryVersion.version = version;
      }
      if (isWaterInstance(object)) {
        const previous = this.instanceVersions.get(object);
        if (
          object.boundingBox === null ||
          geometryChanged ||
          (previous !== undefined &&
            (previous.matrix !== object.instanceMatrix.version ||
              previous.geometry !== version ||
              previous.count !== object.count))
        ) {
          object.computeBoundingBox();
        }
        if (
          previous?.matrix !== object.instanceMatrix.version ||
          previous.geometry !== version ||
          previous.count !== object.count
        ) {
          this.instanceVersions.set(object, {
            matrix: object.instanceMatrix.version,
            geometry: version,
            count: object.count,
          });
        }
        if (object.boundingBox === null) return false;
        this.bounds.copy(object.boundingBox);
      } else {
        if (geometry.boundingBox === null) return false;
        this.bounds.copy(geometry.boundingBox);
      }
      this.bounds.applyMatrix4(object.matrixWorld);
    }
    if (cacheable) {
      const entry = cached ?? {
        geometry,
        position,
        version,
        instanceVersion,
        count,
        matrix: new THREE.Matrix4(),
        bounds: new THREE.Box3(),
      };
      entry.geometry = geometry;
      entry.position = position;
      entry.version = version;
      entry.instanceVersion = instanceVersion;
      entry.count = count;
      entry.matrix.copy(object.matrixWorld);
      entry.bounds.copy(this.bounds);
      this.worldBounds.set(object, entry);
    }
    return underwater
      ? this.bounds.min.y > CREATIVE_WATER_BAND
      : this.bounds.max.y < -CREATIVE_WATER_BAND;
  }
}
